import React, { useEffect, useMemo, useState } from "react"
import { Check, Filter, FilterX, Pencil, Trash, Clipboard } from "lucide-react"
import { useTranslation } from "react-i18next"
import { useDispatch, useSelector } from "react-redux"
import { useNavigate } from "react-router-dom"
import {
  Button,
  Card,
  CardBody,
  Col,
  Container,
  Modal,
  ModalBody,
  ModalHeader,
  Nav,
  NavItem,
  NavLink,
  Row,
  Table,
} from "reactstrap"
import axios from "axios"

import DeleteModal from "components/Common/DeleteModal"
import Spinners from "components/Shared/Spinners"
import {
  API_URL,
  formatDateForAPI,
  handleStopPropagation,
} from "components/Common/Utils"
import Paginations from "components/Common/Pagination"
import MarkAttendance from "./MarkAttendance"
import Filters from "components/Shared/Filters"

import { usePermissions } from "hooks"
import {
  deleteTheoreticalTraining,
  getAllTheoreticalTrainings,
} from "store/actions/theoreticalTrainingAction"
import { showAlert } from "store/reducers/alertReducer"
import {
  resetPayload,
  setPayload,
  toggleFilterVisibility,
  setActiveTab,
} from "store/reducers/theoreticalTrainingReducer"
import CandidateTabs from "./components/CandidateTabs"
import { hideAttendance } from "./helpers"
import moment from "moment/moment"

const navItems = [
  { name: "Training Rooms", id: 0 },
  { name: "Enrolled", id: 1 },
  { name: "Present", id: 2 },
  { name: "Absent", id: 5 },
  { name: "Passed", id: 3 },
  { name: "Failed", id: 4 },
  { name: "Not Invited", id: 6 },
]

const TheoreticalTraining = () => {
  const {
    loading,
    isLoading,
    theoreticalTrainings,
    totalPages,
    totalCount,
    currentPage,
    payload,
    isShowFilter,
    activeTab,
    participantsPayload,
  } = useSelector(state => state.TheoreticalTraining)
  const navigate = useNavigate()
  const { t } = useTranslation()
  document.title = t("Theoretical Training | Yamama")
  const { showAll, permissions } = usePermissions("theoretical_training")
  const { userType } = useSelector(state => state.Login)
  const today = moment().format("DD-MM-YYYY")

  const filters = useMemo(() => {
    return {
      keyword: {
        type: "text",
        placeholder: t("Search by name"),
        label: t("Search By"),
      },
      date_range: { type: "dateRange", label: t("Date Range") },
      // is_saudi: {
      //   type: "select",
      //   placeholder: t("Select Nationality"),
      //   label: t("Select Nationality"),
      //   options: [
      //     { value: "", label: t("All") },
      //     { value: "1", label: t("Saudi") },
      //     { value: "0", label: t("Non Saudi") },
      //   ],
      // },
      order_by: {
        type: "select",
        placeholder: t("Select Order"),
        label: t("Select Order"),
        options: [
          { value: 1, label: t("Ascending") },
          { value: -1, label: t("Descending") },
        ],
      },
    }
  }, [t])

  const dispatch = useDispatch()

  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [itemIdToDelete, setItemIdToDelete] = useState(null)
  const [modal, setModal] = useState(false)
  const [modalType, setModalType] = useState(null)

  const [selectedUuid, setSelectedUuid] = useState(null)
  const [selectedItem, setSelectedItem] = useState(null)
  const [trigger, setTrigger] = useState(false)
  const [isExporting, setIsExporting] = useState(false)

  useEffect(() => {
    if (activeTab === 0) {
      getAllData()
    }
  }, [activeTab, trigger])

  const getAllData = () => {
    let data = { ...payload }
    if (data?.filters?.date_range?.length > 1) {
      data = {
        ...data,
        filters: {
          ...data.filters,
          date_range: {
            start_date: formatDateForAPI(data.filters.date_range[0]),
            end_date: formatDateForAPI(data.filters.date_range[1]),
          },
        },
      }
    } else if (data?.filters?.date_range?.length === 1) {
      data = {
        ...data,
        filters: {
          ...data.filters,
          date_range: {
            start_date: formatDateForAPI(data.filters.date_range[0]),
            end_date: formatDateForAPI(data.filters.date_range[0]),
          },
        },
      }
    } else {
      {
        data = { ...data, filters: { ...data.filters, date_range: {} } }
      }
    }

    dispatch(getAllTheoreticalTrainings(data))
  }

  const handleDelete = () => {
    dispatch(deleteTheoreticalTraining(itemIdToDelete))
      .then(action => {
        const { statusCode, success, message, data } = action?.payload
        if (statusCode === 200 && success) {
          dispatch(showAlert({ text: message, type: "success" }))
          setTrigger(!trigger)
          setShowDeleteModal(false)
        } else {
          dispatch(showAlert({ text: message, type: "error" }))
        }
      })
      .catch(error => {})
  }

  const onPageChange = page => {
    dispatch(setPayload({ page_number: page }))
    setTrigger(!trigger)
  }

  const handleReset = () => {
    dispatch(resetPayload())
    setTrigger(!trigger)
  }

  const toggleTab = tab => {
    if (tab !== activeTab) {
      dispatch(setActiveTab(tab))
      if (tab !== 0) {
        if (isShowFilter !== false) dispatch(toggleFilterVisibility())
      }
      handleReset()
    }
  }

  const toggle = () => {
    setModal(!modal)
    getAllData()
  }
  const handleExportXLSX = async () => {
    try {
      setIsExporting(true)
      const authUserString = localStorage.getItem("authUser")
      const authUser = authUserString ? JSON.parse(authUserString) : null
      const token = authUser?.token

      let data = {
        export_type: "theoretical_participants_list",
        filters: {
          keyword: participantsPayload.filters.keyword || "",
          type: activeTab,
          job_title_ids: participantsPayload.filters.job_title_ids,
        },
      }

      const request =
        activeTab === 0
          ? "api/v1/admin/theoretical/export"
          : "api/v1/admin/export/by-type"

      if (activeTab === 0) {
        data = {
          filters: {
            keyword: payload.filters.keyword,
            date_range:
              payload.filters.date_range.length > 1
                ? {
                    start_date: formatDateForAPI(payload.filters.date_range[0]),
                    end_date: formatDateForAPI(payload.filters.date_range[1]),
                  }
                : {},
          },
        }
      }

      const response = await axios.post(`${API_URL}/${request}`, data, {
        headers: {
          Authorization: token, // Attach token in headers
        },
        responseType: "blob", // Important for file downloads
      })

      const contentType = response.headers["content-type"]

      if (contentType === "application/json") {
        return dispatch(
          showAlert({
            text: t("Export initiated, you will be notified once done."),
            type: "success",
          })
        )
      }

      // Create a URL for the downloaded file
      const url = window.URL.createObjectURL(new Blob([response.data]))
      const link = document.createElement("a")
      link.href = url
      link.setAttribute(
        "download",
        activeTab === 0
          ? `Theoretical Training Rooms ${today}.xlsx`
          : activeTab === 1
          ? `Theoretical Training Enrolled Candidates ${today}.xlsx`
          : activeTab === 2
          ? `Theoretical Training Attended Candidates ${today}.xlsx`
          : activeTab === 3
          ? `Theoretical Training Passed Candidates ${today}.xlsx`
          : activeTab === 4
          ? `Theoretical Training Failed Candidates ${today}.xlsx`
          : activeTab === 5
          ? `Theoretical Training Absent Candidates ${today}.xlsx`
          : `TheoreticalTraining Not Invited Candidates ${today}.xlsx`
      ) // Set filename
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url) // Clean up memory
    } catch (error) {
      console.error("Export failed:", error.response?.data || error.message)
    } finally {
      setIsExporting(false)
    }
  }

  const handleTabFilter = item => {
    if (showAll) {
      return true
    } else if (item.name === "Passed") {
      if (permissions.includes("can_show_passed_tab")) {
        return true
      } else {
        return false
      }
    } else if (item.name === "Failed") {
      if (permissions.includes("can_show_failed_tab")) {
        return true
      } else {
        return false
      }
    } else if (item.name === "Not Invited") {
      if (permissions.includes("can_show_not_invited_tab")) {
        return true
      } else {
        return false
      }
    }
    return true
  }

  if (loading) return <Spinners />

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Row>
            <Col xl="12">
              <div className="d-flex justify-content-between mb-4 align-items-center">
                <h4 className="card-title ">{t("Theoretical Training")}</h4>
                <div className="mb-3 d-flex gap-3">
                  {(showAll || permissions.includes("can_create")) && (
                    <button
                      className="main_btn"
                      onClick={() => {
                        // setModalType("CREATE")
                        // toggle()
                        navigate(`/theoretical-training/create`)
                      }}
                    >
                      {t("Add New")}
                    </button>
                  )}
                  <button
                    className="main_btn fit_content"
                    onClick={handleExportXLSX}
                    disabled={isExporting}
                  >
                    {t("Download XLSX")}
                  </button>

                  {activeTab === 0 && (
                    <Button
                      color="primary"
                      onClick={() => dispatch(toggleFilterVisibility())}
                      title={t("Show/Hide Filter")}
                    >
                      {isShowFilter ? <FilterX /> : <Filter />}
                    </Button>
                  )}
                </div>
              </div>

              <Nav tabs className="one_line_parent mb-3 mt-3">
                {navItems.filter(handleTabFilter).map(item => (
                  <NavItem key={item.id}>
                    <NavLink
                      className={activeTab === item.id ? "active" : ""}
                      onClick={() => toggleTab(item.id)}
                    >
                      {t(item.name)}
                    </NavLink>
                  </NavItem>
                ))}
              </Nav>

              {isShowFilter && (
                <Filters
                  filters={filters}
                  state={{ ...payload.filters, order_by: payload.order_by }}
                  setState={data => {
                    const { order_by, ...rest } = data
                    dispatch(
                      setPayload({
                        order_by: Number(order_by),
                        filters: { ...payload.filters, ...rest },
                      })
                    )
                  }}
                  handleSearch={() => setTrigger(!trigger)}
                  handleReset={handleReset}
                />
              )}

              {activeTab === 0 && (
                <>
                  <Card className="user_personal_info theme_card overflow-x-auto">
                    <CardBody>
                      {loading && theoreticalTrainings.length === 0 ? (
                        <p>{t("No data found")}</p>
                      ) : (
                        <div className="table-responsive">
                          <Table className="table mb-0">
                            <thead className="table-light full_th">
                              <tr>
                                <th>{t("S. No.")}</th>
                                <th>{t("Name")}</th>
                                <th>{t("Building Name")}</th>
                                <th>{t("Job Title")}</th>
                                <th>{t("Capacity")}</th>
                                <th>{t("Confirmed Appointments")}</th>
                                <th>{t("Total Attended")}</th>
                                <th>{t("Date")}</th>
                                <th>{t("Time")}</th>
                                <th>{t("Action")}</th>
                              </tr>
                            </thead>
                            <tbody>
                              {theoreticalTrainings?.map((item, index) => (
                                <tr key={index}>
                                  <th scope="row">
                                    {index +
                                      (payload?.limit || 50) *
                                        (currentPage - 1) +
                                      1}
                                  </th>
                                  <td>
                                    <p
                                      className="text-decoration-underline cursor-pointer"
                                      onClick={() => {
                                        if (
                                          showAll ||
                                          permissions.includes(
                                            "can_view_details"
                                          )
                                        ) {
                                          navigate(
                                            `/theoretical-training/participants?uuid=${
                                              item?.uuid
                                            }&name=${encodeURIComponent(
                                              item?.name
                                            )}`,
                                            {
                                              state: {
                                                end_date: item?.end_date,
                                                end_time: item?.end_time,
                                              },
                                            }
                                          )
                                        }
                                      }}
                                    >
                                      {item?.name}
                                    </p>
                                  </td>
                                  <td>{item?.building_name}</td>
                                  <td>
                                    {item?.job_details?.map((job, index) => (
                                      <p
                                        key={job?.uuid}
                                        className="fit_content"
                                      >
                                        {job?.name}
                                        {index !==
                                          item.job_details.length - 1 && ","}
                                      </p>
                                    ))}
                                  </td>
                                  <td>{item?.trainees_each_group}</td>
                                  <td>{item?.participants_count}</td>
                                  <td>{item?.attendance_count}</td>
                                  <td>
                                    <p className="ltr fit_content">
                                      {item?.start_date} ~ {item?.end_date}
                                    </p>
                                  </td>
                                  <td>
                                    <p className="ltr fit_content">
                                      {item?.start_time} ~ {item?.end_time}
                                    </p>
                                  </td>
                                  <td onClick={handleStopPropagation}>
                                    <div className="d-flex gap-2 action_buttons">
                                      {(showAll ||
                                        permissions.includes("can_edit")) && (
                                        <Button
                                          color="secondary"
                                          onClick={() => {
                                            // setModalType("UPDATE")
                                            // setSelectedUuid(item?.uuid)
                                            // toggle()
                                            navigate(
                                              `/theoretical-training/update?uuid=${item?.uuid}`
                                            )
                                          }}
                                          title={t("Update")}
                                        >
                                          <Pencil />
                                        </Button>
                                      )}
                                      {(showAll ||
                                        permissions.includes(
                                          "can_mark_attendance"
                                        )) &&
                                        !hideAttendance(
                                          item.end_date,
                                          item.end_time
                                        ) && (
                                          <Button
                                            color="primary"
                                            onClick={() => {
                                              setModalType("MARK_ATTENDANCE")
                                              setSelectedUuid(item?.uuid)
                                              setSelectedItem(item)
                                              toggle()
                                            }}
                                            title={t("Mark Attendance")}
                                          >
                                            <Check />
                                          </Button>
                                        )}

                                      {(showAll ||
                                        permissions.includes("can_create")) && (
                                        <Button
                                          color="secondary"
                                          onClick={() => {
                                            navigate(
                                              `/theoretical-training/create?uuid=${item?.uuid}`
                                            )
                                          }}
                                          disabled={
                                            isLoading &&
                                            item?.uuid === itemIdToDelete
                                          }
                                          title={t("Clone")}
                                        >
                                          <Clipboard />
                                        </Button>
                                      )}
                                      {(showAll ||
                                        permissions.includes("can_delete")) && (
                                        <Button
                                          color="danger"
                                          onClick={() => {
                                            setItemIdToDelete(item.uuid)
                                            setShowDeleteModal(true)
                                          }}
                                          disabled={
                                            isLoading &&
                                            item?.uuid === itemIdToDelete
                                          }
                                          title={t("Delete")}
                                        >
                                          <Trash />
                                        </Button>
                                      )}
                                    </div>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </Table>
                        </div>
                      )}
                    </CardBody>
                  </Card>
                  {!loading && theoreticalTrainings.length > 0 && (
                    <Paginations
                      currentPage={currentPage}
                      totalPages={totalPages}
                      totalCount={totalCount}
                      onPageChange={onPageChange}
                    />
                  )}
                </>
              )}
              {activeTab !== 0 && <CandidateTabs activeTab={activeTab} />}
            </Col>
          </Row>
        </Container>
      </div>
      <DeleteModal
        show={showDeleteModal}
        onDeleteClick={handleDelete}
        onCloseClick={() => {
          setItemIdToDelete(null)
          setShowDeleteModal(false)
        }}
        isLoading={isLoading}
        buttonText={t("Delete")}
        bodyTitle={t(
          "Are you sure you want to delete this theoretical training?"
        )}
      />
      <Modal
        isOpen={modal}
        toggle={toggle}
        size="lg"
        className=""
        backdrop="static"
      >
        <ModalHeader toggle={toggle}>
          {t("Mark Attendance")} - {selectedItem?.name}
        </ModalHeader>
        <ModalBody>
          <MarkAttendance
            selectedUuid={selectedUuid}
            selectedItem={selectedItem}
            toggle={toggle}
          />
        </ModalBody>
      </Modal>
    </React.Fragment>
  )
}

export default TheoreticalTraining
